import { storedCampaignCode } from "@/domain/attribution/attribution"
import { isTrackingOptedOut } from "@/domain/storefront-analytics/session"
import { apiCustomerURL } from "@/consts"
import { resolveStorefrontSubdomain } from "@/lib/ensure-company"

/** Funnel step a campaign visitor reached, as the gateway names it. */
export type EngagementStep = "visit" | "add_to_cart" | "checkout_started"

const SENT_PREFIX = "meucontroleai:engagement:"

function sentKey(merchantId: string, code: string, step: EngagementStep): string {
  return `${SENT_PREFIX}${merchantId}:${code}:${step}`
}

function alreadySent(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null
  } catch {
    return false
  }
}

function markSent(key: string): void {
  try {
    sessionStorage.setItem(key, "1")
  } catch {
    return
  }
}

/**
 * Tells the gateway that the visitor who arrived through a campaign link reached `step`, at most once per
 * (code, step) per tab session. Fire-and-forget (`fetch` keepalive): never throws, never awaits, sends
 * nothing without a stored campaign code or under Do Not Track / Global Privacy Control.
 */
export function recordCampaignEngagement(merchantId: string, step: EngagementStep): void {
  try {
    if (isTrackingOptedOut()) return
    const code = storedCampaignCode(merchantId)
    if (!code) return
    const key = sentKey(merchantId, code, step)
    if (alreadySent(key)) return
    markSent(key)
    const url = `${apiCustomerURL}/storefront/${encodeURIComponent(resolveStorefrontSubdomain())}/marketing/engagement`
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, step }),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    return
  }
}
